import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider } from '@/context/AuthContext';
import PrivateRoute from '@/components/auth/PrivateRoute';
import Layout from '@/components/Layout/Layout';
import Login from '@/pages/Login';
import Signup from '@/pages/Signup';
import AuthCallback from '@/pages/AuthCallback';
import PendingApproval from '@/pages/PendingApproval';
import Dashboard from '@/pages/Dashboard';
import Companies from '@/pages/Companies';
import AddCompany from '@/pages/AddCompany';
import Jobs from '@/pages/Jobs';
import JobDetail from '@/pages/JobDetail';
import Invoices from '@/pages/Invoices';
import FilingHistory from '@/pages/FilingHistory';
import Referrals from '@/pages/Referrals';
import Settings from '@/pages/Settings';
import Onboarding from '@/pages/Onboarding';
import RequireAccountSetup from '@/components/auth/RequireAccountSetup';
import ResetPassword from '@/pages/ResetPassword';
import StaffSession from '@/pages/StaffSession';
import Step1Options from '@/pages/wizard/Step1Options';
import Step2Companies from '@/pages/wizard/Step2Companies';
import Step3Review from '@/pages/wizard/Step3Review';
import Step4Upload from '@/pages/wizard/Step4Upload';
import QuotePage from '@/pages/wizard/QuotePage';
import WizardConverter from '@/pages/wizard/WizardConverter';
import WizardExpert from '@/pages/wizard/WizardExpert';
import WizardCompletion from '@/pages/wizard/WizardCompletion';
import { Analytics } from '@vercel/analytics/react';
import { ThemeProvider } from 'next-themes';
import ErrorBoundary from '@/components/shared/ErrorBoundary';
import '@/App.css';

function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
    <ErrorBoundary>
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public auth routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/auth/staff" element={<StaffSession />} />
          <Route path="/verify-email" element={<AuthCallback />} />
          <Route path="/pending-approval" element={<PendingApproval />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* Post-signup onboarding — requires auth */}
          <Route path="/onboarding" element={
            <PrivateRoute><Onboarding /></PrivateRoute>
          } />

          {/* Main app routes with sidebar layout — all require auth */}
          <Route path="/" element={
            <PrivateRoute><RequireAccountSetup><Layout /></RequireAccountSetup></PrivateRoute>
          }>
            <Route index element={<Dashboard />} />
            <Route path="companies" element={<Companies />} />
            <Route path="companies/add" element={<AddCompany />} />
            <Route path="jobs" element={<Jobs />} />
            <Route path="jobs/:jobId" element={<JobDetail />} />
            <Route path="invoices" element={<Invoices />} />
            <Route path="history" element={<FilingHistory />} />
            <Route path="referrals" element={<Referrals />} />
            <Route path="settings" element={<Settings />} />
            <Route path="wizard/start" element={<Step1Options />} />
            <Route path="wizard/companies" element={<Step2Companies />} />
            <Route path="wizard/review" element={<Step3Review />} />
            <Route path="wizard/upload" element={<Step4Upload />} />
            <Route path="wizard/quote" element={<QuotePage />} />
          </Route>

          {/* Full-screen wizard pages — require auth */}
          <Route path="/wizard/converter" element={
            <PrivateRoute><WizardConverter /></PrivateRoute>
          } />
          <Route path="/wizard/expert" element={
            <PrivateRoute><WizardExpert /></PrivateRoute>
          } />
          <Route path="/wizard/completion" element={
            <PrivateRoute><WizardCompletion /></PrivateRoute>
          } />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
      <Analytics />
    </AuthProvider>
    </ErrorBoundary>
    </ThemeProvider>
  );
}

export default App;
